import { useEffect, useState } from 'react';
import qs from 'query-string';
import api from 'api';
import { API_SCORECARD_SEARCH_REVIEW_CYCLES } from 'constants/api';
import { SCORECARD_STATUS_DELETED } from 'constants/scorecards';
import { ScorecardFilters, ScorecardStores } from 'store/interfaces';
import { storeName as directReportsStoreName } from 'store/scorecards/direct/config';
import { storeName as subordinatesStoreName } from 'store/scorecards/subordinates/config';

export interface AvailableReviewCycle {
  id: number;
  name: string;
  startAt: string;
  endAt: string;
}

interface Args {
  storeName: ScorecardStores;
  filters: ScorecardFilters;
  isArchived?: boolean;
  isDeleted?: boolean;
  /** Set false to skip fetching (e.g. flat-list tabs that don't group). */
  enabled?: boolean;
}

const tabScopeParams = (storeName: ScorecardStores) => {
  switch (storeName) {
    case directReportsStoreName:
      return { subordinates: 1 };
    case subordinatesStoreName:
      return { indirectSubordinates: 1 };
    default:
      return {};
  }
};

export default function useAvailableReviewCycles({
  storeName,
  filters,
  isArchived,
  isDeleted,
  enabled = true,
}: Args) {
  const [cycles, setCycles] = useState<AvailableReviewCycle[]>([]);
  const [loading, setLoading] = useState(false);
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);

    const queryParams = {
      ...tabScopeParams(storeName),
      ...filters,
      ...(isArchived ? { archive: true } : {}),
      ...(isDeleted ? { scorecardStatuses: SCORECARD_STATUS_DELETED } : {}),
    };

    api.axios
      .get<{ cycles: AvailableReviewCycle[] }>(
        `${API_SCORECARD_SEARCH_REVIEW_CYCLES}?${qs.stringify(queryParams, {
          skipNull: true,
          skipEmptyString: true,
        })}`,
      )
      .then((res) => {
        if (cancelled) return;
        setCycles(res.data?.cycles ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setCycles([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `filters` identity changes on every redux dispatch; depend on the
    // stringified form so the effect only refires when actual filter values
    // change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storeName, filtersKey, isArchived, isDeleted]);

  return { cycles, loading };
}
